// [목적] WC-ENRICH-15 — PlanItem / StreamDayPlanItem 의 풍부화 필드(activity·tip·factInfo·firstImage)를
//         공통 렌더링 블록으로 캡슐화. FolioPage(뷰어)와 FolioDayCardPreview(미리보기) 양쪽에서 재사용.
// [이유] folio-enrichment Spec §5 화면: activity(활동) + tip(꿀팁) + factInfo(주소/전화/영업시간) info chip +
//         firstImage 썸네일을 하나의 재사용 컴포넌트로 제공하여 두 진입점의 코드 중복을 제거한다.
//         디자인 토큰만 사용(CLAUDE §6-3), 접근성 WCAG 2.1 AA(§6-4).
// [사이드 이팩트] FolioPage.tsx · FolioDayCardPreview.tsx 에서 import.
//                 factInfo.tel 링크 클릭 시 tel: 프로토콜 실행(모바일 전화 앱 열림).
//                 firstImage onError 핸들러가 img 요소를 DOM 에서 display:none 처리.
// [수정 시 고려사항] folio.ts planItemSchema 의 activity/tip/factInfo/firstImage 필드가 변경되면 동기 갱신.
//                   이미지는 aspect-ratio 1.78(16:9) 고정 — CLS 방지(R6). 변경 시 CLS 재검증 필요.
//                   disp:none 폴백 전략: onError 로 이미지 숨김, 플레이스홀더 없음(매거진 레이아웃 보존).
//                   tel: 링크는 rel="noopener noreferrer" 불필요(동일 origin 이동 아님·external 아님).

import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';
import { useRef, type CSSProperties } from 'react';

const c = tokens.color;
const R = tokens.radius;
const SP = tokens.spacing;
const L = 'light' as const;

// ─── 풍부화 필드 Props 인터페이스 ─────────────────────────────────────────

/**
 * [목적] PlanItemEnrichedContent 가 수용하는 최소 인터페이스 — planItemSchema 풍부화 필드 부분집합
 * [이유] FolioPage(PlanItem) 과 FolioDayCardPreview(StreamDayPlanItem) 양쪽이 이 인터페이스를
 *         구조적 서브타입으로 만족하도록 공통 타입으로 분리.
 * [사이드 이팩트] 소비자가 이 인터페이스에 맞는 부분집합 prop 만 전달하면 됨.
 * [수정 시 고려사항] planItemSchema.factInfo 구조(folio.ts)와 항상 일치 유지.
 */
export interface EnrichedItemFields {
  /** 시간대별 구체 활동 (LLM 생성). null/undefined 이면 미표시. */
  activity?: string | null;
  /** 현지 꿀팁 (LLM 생성). null/undefined 이면 미표시. */
  tip?: string | null;
  /** 사실정보 원문 — 주소/전화/영업시간/주차/휴무 (TourAPI 원문, LLM 변형 금지). null/undefined 이면 미표시. */
  factInfo?: {
    addr?: string | null;
    tel?: string | null;
    usetime?: string | null;
    parking?: string | null;
    restdate?: string | null;
    /** TourAPI 대표 이미지 URL(원문). BE: candidate.firstImage → factInfo.imageUrl jsonb. */
    imageUrl?: string | null;
  } | null;
  /** (호환) 상위 레벨 이미지 URL. 우선순위는 factInfo.imageUrl > firstImage. null/undefined 이면 미표시. */
  firstImage?: string | null;
  /** 이미지 alt 텍스트용 장소명 */
  title?: string | null;
}

export interface PlanItemEnrichedContentProps {
  item: EnrichedItemFields;
  /** 컴팩트 모드 — 미리보기(FolioDayCardPreview)에서 사용. 이미지·factInfo 일부 생략 가능. 기본 false */
  compact?: boolean;
  sx?: SxProps<Theme>;
}

// ─── 이미지 썸네일 ────────────────────────────────────────────────────────

/**
 * [목적] TourAPI firstImage 썸네일 — lazy loading + aspect-ratio 고정 + onError 폴백
 * [이유] R6(FE 핫링크/로딩): CLS 방지(aspect-ratio 고정), 깨진 이미지 숨김(onError display:none),
 *         loading="lazy"로 초기 로드 지연.
 * [사이드 이팩트] onError 시 img 요소 display:none — 부모 Box 는 공간 0으로 축소(auto height).
 * [수정 시 고려사항] aspect-ratio 16/9 고정(1.78). 이미지 비율이 다를 경우 object-fit:cover 로 크롭.
 *                   alt 는 poiName — 빈 문자열 허용(장식 이미지), 접근성 §6-4.
 */
function ItemThumbnail({ src, alt }: { src: string; alt: string }): JSX.Element {
  const imgRef = useRef<HTMLImageElement>(null);

  // [목적] 이미지 로드 실패 시 요소 숨김 처리 (R6 onError 폴백)
  // [이유] 깨진 이미지 아이콘 노출 방지 + 레이아웃 shift 방지 (display:none → 공간 0)
  // [사이드 이팩트] 없음 (img 요소에만 영향)
  // [수정 시 고려사항] 플레이스홀더 이미지가 필요하면 onError 에서 src 교체 방식으로 변경
  const handleError: CSSProperties = {} as CSSProperties; // 타입 단언 대신 ref 방식 사용
  void handleError;

  return (
    <Box
      sx={{
        width: '100%',
        // aspect-ratio 16/9 — CSS 토큰이 없으므로 aspect-ratio 값은 레이아웃 고정 상수(CLS 방지 목적)
        // 디자인 토큰이 없는 layout 고정값 — CLS 방지를 위한 예외 허용 (CLAUDE §6-3 주석 예외 인식)
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        borderRadius: `${R.s}px`,
        bgcolor: c.paper[2][L],
        mb: `${SP[2]}px`,
      }}
    >
      <Box
        ref={imgRef}
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => {
          if (imgRef.current) {
            imgRef.current.style.display = 'none';
            // 부모 Box 도 숨김 처리 — 빈 aspect-ratio 박스 노출 방지
            const parent = imgRef.current.parentElement;
            if (parent) parent.style.display = 'none';
          }
        }}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  );
}

// ─── activity 블록 ────────────────────────────────────────────────────────

/**
 * [목적] activity(활동) 텍스트 블록 — 라벨과 함께 story 위에 표시
 * [이유] folio-enrichment §4 AC: activity 는 시간대별 "무엇을 하라"는 실행 정보 — story(감성 서사)와 구분.
 *         라벨 "활동" 텍스트로 색만으로 정보 전달 금지 준수(WCAG §1.4.1).
 * [사이드 이팩트] 없음 (순수 표시)
 * [수정 시 고려사항] 색 대비: c.brand[500].light(#1F6FEB) 위 white — 단, activity 텍스트는 c.ink.high 로 표시.
 */
function ActivityBlock({ text }: { text: string }): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: `${SP[1]}px`,
        mb: `${SP[1]}px`,
      }}
    >
      {/* 라벨 칩 */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: `${c.brand[500][L]}15`,
          border: `1px solid ${c.brand[500][L]}30`,
          borderRadius: `${R.s}px`,
          px: `${SP[1]}px`,
          py: '2px',
          mt: '1px',
        }}
      >
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            color: c.brand[500][L],
            lineHeight: 1.3,
            letterSpacing: '0.3px',
          }}
        >
          활동
        </Typography>
      </Box>
      {/* 활동 텍스트 */}
      <Typography
        sx={{
          fontSize: 11,
          color: c.ink.high[L],
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// ─── tip 박스 ─────────────────────────────────────────────────────────────

/**
 * [목적] tip(꿀팁) 강조 박스 — warm 색상 계열로 story 와 시각 구분
 * [이유] folio-enrichment §4 AC: tip 은 LLM 이 factInfo 데이터 근거로 생성한 꿀팁 — 별도 강조 박스.
 *         warm.500 배경으로 브랜드/accent 와 시각 분리. "꿀팁" 텍스트 라벨 병기(WCAG §1.4.1).
 * [사이드 이팩트] 없음 (순수 표시)
 * [수정 시 고려사항] c.warm.500.light(#F2A65A) 위 ink.high(#29261B) 대비 확인 필요 — 배경은 15% 투명도라 PAPER0 기반.
 *                   배경 PAPER0(#FFF)+warm15% 혼합 대비: 텍스트 c.ink.high (#29261B) ≈ 5.8:1 (AA 충족).
 */
function TipBox({ text }: { text: string }): JSX.Element {
  return (
    <Box
      role="note"
      aria-label="꿀팁"
      sx={{
        bgcolor: `${c.warm[500][L]}18`,
        border: `1px solid ${c.warm[500][L]}40`,
        borderRadius: `${R.s}px`,
        px: `${SP[2]}px`,
        py: `${SP[1]}px`,
        mb: `${SP[1]}px`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: `${SP[1]}px`,
      }}
    >
      {/* 꿀팁 아이콘 — aria-hidden, 텍스트 라벨이 정보 전달 담당(WCAG §1.4.1) */}
      <Typography aria-hidden="true" sx={{ fontSize: 12, lineHeight: 1.5, flexShrink: 0 }}>
        💡
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: c.ink.high[L],
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// ─── factInfo info chip 행 ─────────────────────────────────────────────────

/**
 * [목적] factInfo 사실정보를 info chip 행으로 렌더 — addr/tel/usetime 각 chip
 * [이유] folio-enrichment §4 AC(OQ-h): 주소·전화·영업시간을 아이콘+텍스트 chip 으로 구분 표시.
 *         색만으로 정보 전달 금지(WCAG §1.4.1) — 아이콘+텍스트 병기.
 *         없는 필드는 chip 미표시(graceful). 전화는 tel: 링크.
 * [사이드 이팩트] tel: 링크 클릭 시 모바일 전화 앱 실행.
 * [수정 시 고려사항] factInfo 필드 추가 시(e.g. parking/restdate) 여기에 chip 추가.
 *                   chip 배경 c.paper[2].light(#F2F0EB) + 텍스트 c.ink.mid (#5C5849): 대비 ≈ 5.1:1 (AA 충족).
 */
function FactInfoChips({
  factInfo,
  compact,
}: {
  factInfo: NonNullable<EnrichedItemFields['factInfo']>;
  compact: boolean;
}): JSX.Element | null {
  const chips: Array<{ key: string; icon: string; label: string; href?: string }> = [];

  if (factInfo.addr) {
    chips.push({ key: 'addr', icon: '📍', label: factInfo.addr });
  }
  if (factInfo.tel) {
    chips.push({
      key: 'tel',
      icon: '📞',
      label: factInfo.tel,
      href: `tel:${factInfo.tel.replace(/\s/g, '')}`,
    });
  }
  if (factInfo.usetime) {
    chips.push({ key: 'usetime', icon: '🕐', label: factInfo.usetime });
  }
  // compact 모드에서는 addr 만 생략하고 tel/usetime 유지 (너무 긴 주소 미리보기에서 스킵)
  const visibleChips = compact ? chips.filter((c) => c.key !== 'addr') : chips;

  if (visibleChips.length === 0) return null;

  return (
    // [수정: 디자인 개편 2026-09-20] 칩을 항상 세로로 쌓아 짧은 칩("상시 개방" 등)도 한 줄을 통째로
    // 써버려 카드가 불필요하게 길어졌다. 가로 wrap 으로 바꿔 폭이 남으면 나란히 붙인다.
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      spacing={`${SP[1]}px`}
      sx={{ mt: `${SP[1]}px` }}
      aria-label="장소 정보"
      role="group"
    >
      {visibleChips.map((chip) => {
        const inner = (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'flex-start',
              gap: `${SP[1]}px`,
              // 칩은 보조 메타이므로 면(bgcolor) 대신 헤어라인으로 낮춰 본문과 경쟁하지 않게 한다.
              bgcolor: 'transparent',
              border: `1px solid ${c.line.base[L]}`,
              borderRadius: `${R.xl}px`,
              px: `${SP[2]}px`,
              py: '3px',
              maxWidth: '100%',
            }}
          >
            {/* 아이콘 — aria-hidden, 라벨이 정보 전달(WCAG §1.4.1) */}
            <Typography aria-hidden="true" sx={{ fontSize: 10, lineHeight: 1.6, flexShrink: 0 }}>
              {chip.icon}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                color: c.ink.mid[L],
                lineHeight: 1.5,
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
              }}
            >
              {chip.label}
            </Typography>
          </Box>
        );

        if (chip.href) {
          return (
            <Box
              key={chip.key}
              component="a"
              href={chip.href}
              aria-label={`전화: ${chip.label}`}
              sx={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                borderRadius: `${R.s}px`,
                // focus-visible 인디케이터 (WCAG §2.4.7)
                '&:focus-visible': {
                  outline: `2px solid ${c.brand[500][L]}`,
                  outlineOffset: '2px',
                },
              }}
            >
              {inner}
            </Box>
          );
        }

        return (
          <Box key={chip.key} sx={{ display: 'block' }}>
            {inner}
          </Box>
        );
      })}
    </Stack>
  );
}

// ─── 메인 컴포넌트 ─────────────────────────────────────────────────────────

/**
 * [목적] PlanItem 풍부화 렌더링 블록 — activity·tip·factInfo·firstImage 를 단일 컴포넌트로 통합 제공
 * [이유] WC-ENRICH-15: FolioPage(뷰어)·FolioDayCardPreview(미리보기) 양쪽에서 동일 풍부화 렌더를 재사용.
 *         각 필드가 null/undefined 이면 해당 섹션 미표시(graceful), 기존 note/poiName/scheduledTime 렌더는 보존.
 * [사이드 이팩트] FolioPage·FolioDayCardPreview 아이템 블록 내에 삽입 — 기존 렌더 순서 변경 없음.
 * [수정 시 고려사항] compact=true(미리보기): 이미지는 표시, addr chip 생략. compact=false(뷰어): 전체 표시.
 *                   all-null 시 null 반환으로 DOM 노드 0 — 기존 레이아웃 영향 없음.
 */
export function PlanItemEnrichedContent({
  item,
  compact = false,
  sx,
}: PlanItemEnrichedContentProps): JSX.Element | null {
  const { activity, tip, factInfo, firstImage, title } = item;

  // [목적] 썸네일 소스 우선순위 — factInfo.imageUrl(BE 정본) > firstImage(상위 호환) + http(s) 스킴 가드
  // [이유] BE 가 candidate.firstImage 원문을 factInfo.imageUrl jsonb 로 흘려보냄(WC-ENRICH 배선).
  //         FolioPage 는 item 통째 전달(상위 firstImage 없음)이라 factInfo.imageUrl 을 읽어야 이미지가 뜸.
  //         보안 Low-1(wave4): 오염된 DB 값(data: 대용량·외부 추적 픽셀) 방어 — http(s) URL 만 허용, 그 외 미표시.
  // [사이드 이팩트] 없음 — 둘 다 없거나 비-http(s) 면 null → 미표시(graceful, onError 폴백과 동일 UX).
  // [수정 시 고려사항] 스킴 화이트리스트는 /^https?:\/\//i — 프로토콜 상대 URL(//host) 추가 시 정규식 갱신.
  const rawImageSrc = factInfo?.imageUrl ?? firstImage ?? null;
  const imageSrc = rawImageSrc && /^https?:\/\//i.test(rawImageSrc) ? rawImageSrc : null;

  // 모든 풍부화 필드가 비어있으면 null 반환 (graceful — 기존 렌더 영향 없음)
  const hasAny =
    !!activity ||
    !!tip ||
    (factInfo && (factInfo.addr || factInfo.tel || factInfo.usetime)) ||
    !!imageSrc;

  if (!hasAny) return null;

  return (
    <Box sx={sx}>
      {/* 썸네일 — lazy + onError 폴백 + aspect-ratio 고정(R6). 소스: factInfo.imageUrl > firstImage */}
      {imageSrc && <ItemThumbnail src={imageSrc} alt={title ?? ''} />}

      {/* activity 블록 — story 바로 아래 위치 */}
      {activity && <ActivityBlock text={activity} />}

      {/* tip 강조 박스 */}
      {tip && <TipBox text={tip} />}

      {/* factInfo info chip 행 — addr/tel/usetime */}
      {factInfo && (factInfo.addr || factInfo.tel || factInfo.usetime) && (
        <FactInfoChips factInfo={factInfo} compact={compact} />
      )}
    </Box>
  );
}
